/**
 * @Author: Zero
 * @Date: 2024-10-21 23:22:25
 * @Explain: 级联选择器
 */

import { intlGet } from '@/locales';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PopupVisibleChangeContext } from 'tdesign-react';
import { Loading, Popup } from 'tdesign-react';
// utils
import { getDefaultValueByType } from '@/utils/common/dataConversion';
import { isArrayLen } from '@/utils/common/dataJudgment';
import { onCascaderSelectRenderText } from '../const';
// type
import type { CascaderOptionListProps, CascaderSelectProps } from '../data.d';
import { InputSizeEnum } from '../data.d';
// components
import CascaderOptionList from '../cascaderOptionList';
import CascaderCustom from './cascaderCustom';

type PropsType = {
  children?: ReactNode;
  /** 级联列表属性 */
  cascaderOptionListProps?: Partial<CascaderOptionListProps>;
  /** 列表宽度 */
  listWidth?: number | string;
} & CascaderSelectProps;
const CascaderSelect = (props: PropsType) => {
  const {
    className,
    children,
    footer,
    listHeader,
    value,
    placeholder = intlGet('COMMON.PLEASE.SELECT'),
    size = InputSizeEnum.M,
    allOption,
    multiple = false,
    expanded = false,
    searchable,
    searchPlaceholder,
    disabled,
    loading = false,
    shadow = true,
    shadowIsClose = true,
    clearable,
    autoWidth = false,
    matchButtonWidth = true,
    borderless,
    displayText,
    displayTextType,
    prefixIcon,
    prefixIconName,
    suffixIcon,
    suffixIconName,
    emptyText,
    isGroup = false,
    groupKey = 'group',
    listMaxHeight,

    displayTextKey = 'label',
    displayOptions = [],
    options,
    customOption,

    // popup - props
    trigger = 'click',
    placement = 'bottom-left',
    maxColumnWidth = 'auto',
    minColumnWidth,
    overlayClassName,
    overlayInnerStyle,
    overlayStyle,
    popupProps,

    // tag - input - props
    minCollapsedNum,
    collapsedItems,
    valueDisplay,

    // 级联列表
    cascaderOptionListProps,
    listWidth,

    // 选中结果框回显效果
    fullPathName = true,
    // 选中结果框渲染key
    displaySelectedResTextKey = 'label',

    onClick,
    onChange,
    onClose,
    onClearable,
  } = props;

  const dropdownInputRef = useRef<HTMLDivElement>(null);
  // 下拉项内容Ref
  const contentRef = useRef<any>(null);
  // 同步存储visible情况
  const visibleRef = useRef<any>(null);

  const [visible, setVisible] = useState(false);
  const [popupWidth, setPopupWidth] = useState<any>('auto');

  /** 文本渲染逻辑 - 此功能暂不完全 */
  const valueText = useMemo(
    () =>
      isArrayLen(options)
        ? onCascaderSelectRenderText({
            options,
            value,
            config: { displayOptions },
          })
        : onCascaderSelectRenderText({
            options: displayOptions,
            value,
            config: { displayOptions: [] },
          }),
    [value, options],
  );

  const customPopupProps = useMemo(() => {
    const config: any = {
      visible,
      trigger,
      placement,
      disabled,
    };
    // 当hover时，使用组件内置开启隐藏
    if (trigger === 'hover') {
      delete config.visible;
    }
    return config;
  }, [visible, trigger, placement, disabled]);

  const shadowRender = useMemo(
    () =>
      visible &&
      shadow && (
        <div
          className="popup-shadow"
          onClick={() => {
            setVisible(false);
            onClose?.();
          }}
        />
      ),
    [visible, shadow, shadowIsClose, setVisible, onClose],
  );

  const popupOnVisibleChange = (
    visible: boolean,
    context: PopupVisibleChangeContext,
  ) => {
    if (matchButtonWidth) {
      const width = dropdownInputRef.current?.offsetWidth || 0;

      if (width > 0) {
        setPopupWidth(width);
      }
    }
  };

  // 监听点击下拉项范围外 - 执行关闭
  const onCloseListener = (event?: any) => {
    // 组件关闭 或 组件已被卸载，元素已不存在，则无需再向下执行关闭
    if (
      !visibleRef.current ||
      [contentRef?.current, dropdownInputRef?.current].includes(null)
    ) {
      return;
    }

    // 点击事件是否落下下拉项内
    const isClickListDom =
      contentRef?.current && contentRef.current.contains(event.target);
    // 点击事件是否落下触发展开下拉项Dom
    const isClickOpenDom =
      dropdownInputRef?.current &&
      dropdownInputRef?.current?.contains(event.target);

    // 组件已被卸载，元素已不存在，则无需再向下执行关闭
    if ([contentRef?.current, dropdownInputRef?.current].includes(null)) return;

    // 关闭下拉逻辑
    if (!isClickListDom && !isClickOpenDom) {
      popupOnVisibleChange(false, {});
      setVisible(false);
      onClose?.();
    }
  };
  // 阻止滚动
  const preventScroll = (event: any) => event.preventDefault();

  useEffect(() => {
    visibleRef.current = visible;
    window.addEventListener('click', onCloseListener);
    const $layoutBodyDom: any = document.getElementsByClassName(
      'smartlink-layout__body',
    )[0];
    visible &&
      $layoutBodyDom &&
      $layoutBodyDom.addEventListener('wheel', preventScroll);
    return () => {
      $layoutBodyDom &&
        $layoutBodyDom.removeEventListener('wheel', preventScroll);
    };
  }, [visible]);
  return (
    <>
      <Popup
        destroyOnClose={true}
        {...customPopupProps}
        overlayClassName={classNames(
          'default-td-cascader-select',
          overlayClassName,
        )}
        overlayStyle={{
          ...overlayStyle,
        }}
        overlayInnerStyle={{
          width: popupWidth,
          minWidth: minColumnWidth,
          maxWidth: maxColumnWidth,
          ...overlayInnerStyle,
        }}
        content={
          <div ref={contentRef} onClick={(e) => e?.stopPropagation()}>
            {loading ? (
              <div
                className="pd-20 text-align-center"
                style={{ width: listWidth || popupWidth }}
              >
                <Loading size="small" />
              </div>
            ) : (
              <>
                <CascaderOptionList
                  {...cascaderOptionListProps}
                  searchable={searchable}
                  allOption={allOption}
                  multiple={multiple}
                  options={options}
                  value={value}
                  isGroup={isGroup}
                  groupKey={groupKey}
                  searchPlaceholder={searchPlaceholder}
                  displayTextKey={displayTextKey}
                  emptyText={emptyText}
                  listWidth={listWidth}
                  listMaxHeight={listMaxHeight}
                  listHeader={listHeader}
                  customOption={customOption}
                  onClose={() => {
                    setVisible(false);
                    onClose?.();
                  }}
                  onChange={onChange}
                  onClick={(val, item) => {
                    setVisible(false);
                    onChange?.(val, item);
                    onClick?.(val, item);
                  }}
                />
                {footer}
              </>
            )}
          </div>
        }
        onVisibleChange={popupOnVisibleChange}
        {...popupProps}
      >
        <span
          ref={dropdownInputRef}
          // 当full的时候才100%，其他情况自适应
          className={classNames({
            'width-100': size === InputSizeEnum.FULL,
          })}
          onClick={() => {
            if (!disabled) {
              setVisible((val) => !val);
            }
          }}
        >
          {children || (
            <CascaderCustom
              isCascader
              className={className}
              displayText={displayText}
              displayTextKey={displayTextKey}
              displayTextType={displayTextType}
              valueText={valueText}
              fullPathName={fullPathName}
              displaySelectedResTextKey={displaySelectedResTextKey}
              visible={visible}
              value={value}
              loading={loading}
              disabled={disabled}
              clearable={clearable}
              borderless={borderless}
              multiple={multiple}
              placeholder={placeholder}
              size={size}
              autoWidth={autoWidth}
              prefixIcon={prefixIcon}
              prefixIconName={prefixIconName}
              suffixIcon={suffixIcon}
              suffixIconName={suffixIconName}
              minCollapsedNum={minCollapsedNum}
              collapsedItems={collapsedItems}
              valueDisplay={valueDisplay}
              onClearable={() => {
                if (onClearable) {
                  onClearable?.();
                }
                onChange?.(getDefaultValueByType(value), {
                  trigger: 'clear',
                });
              }}
              onTagRemove={({ index }) => {
                const newValue = [...value];
                newValue.splice(index, 1);
                onChange?.(newValue, {
                  trigger: 'tagRemove',
                });
              }}
            />
          )}
        </span>
      </Popup>
      {/* 阴影层 */}
      {shadowRender}
    </>
  );
};

export default CascaderSelect;
